import React from 'react';
import { Row, Col, Skeleton } from 'antd';
import {
    DataCard,
    EmptyCard,
    ChartPlaceholder,
    EnrollmentFunnelChart,
    DirectDepositSwitchesChart,
    ActiveUsersOverTimeChart,
    AdvancesOverTimeChart,
    AdvancesAndNonInterestIncomeChart,
    DirectDepositVolumeAndNetInterestIncomeChart
} from './';
import styles from './styles/dashboard-template.module.scss';

function DashboardTemplate({ title, topKPIs = [], charts = [], bottomKPIs = [], bottomTitle, report = {}, loading = false }) {
    const renderKPICard = (kpi, index) => {
        const { key, title, percentageKey, showInfo = false, isCurrency = false, showProgress = false } = kpi;

        if (loading) {
            return (
                <div style={{ background: '#fff', padding: '24px', borderRadius: '8px' }}>
                    <Skeleton active paragraph={{ rows: 0 }} />
                    <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1 }} />
                </div>
            );
        }

        const value = report[key];
        const percentage = report[percentageKey];

        if (value) {
            return (
                <DataCard
                    title={title}
                    value={value}
                    percentage={percentage || 0}
                    showInfo={showInfo}
                    isCurrency={isCurrency}
                    showProgress={showProgress}
                    index={index}
                />
            );
        } else {
            return <EmptyCard title={title} showInfo={showInfo} index={index} />;
        }
    };

    const renderChart = (chart) => {
        const { title, type, data } = chart;

        if (loading) {
            return (
                <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', height: '400px' }}>
                    <Skeleton active paragraph={{ rows: 8 }} />
                </div>
            );
        }

        // Render specific chart types
        switch (type) {
            case 'enrollmentFunnel':
                return <EnrollmentFunnelChart title={title} data={report.enrollmentFunnelData || data} />;
            case 'directDepositSwitches':
                return <DirectDepositSwitchesChart title={title} data={report.directDepositSwitchesData || data} />;
            case 'activeUsersOverTime':
                return <ActiveUsersOverTimeChart title={title} data={report.activeUsersOverTimeData || data} />;
            case 'advancesOverTime':
                return <AdvancesOverTimeChart title={title} data={report.advancesOverTimeData || data} />;
            case 'advancesAndNonInterestIncome':
                return <AdvancesAndNonInterestIncomeChart title={title} data={report.advancesAndNonInterestIncomeData || data} />;
            case 'directDepositVolumeAndNetInterestIncome':
                return <DirectDepositVolumeAndNetInterestIncomeChart title={title} data={report.directDepositVolumeAndNetInterestIncomeData || data} />;
            default:
                return <ChartPlaceholder title={title} />;
        }
    };

    return (
        <div className={styles.dashboardTemplate}>
            <h2 className={styles.sectionTitle}>{title}</h2>

            {/* Top Row - First 4 KPI Cards */}
            {topKPIs.length > 0 && (
                <Row gutter={0} className={styles.cardsRow}>
                    {topKPIs.map((kpi, index) => (
                        <Col xs={24} sm={12} md={12} lg={6} key={`top-${index}`}>
                            {renderKPICard(kpi, index)}
                        </Col>
                    ))}
                </Row>
            )}

            {/* Middle Row - Charts */}
            {charts.length > 0 && (
                <Row gutter={[0, 20]} className={styles.chartsRow}>
                    {charts.map((chart, index) => (
                        <Col xs={24} sm={24} md={12} lg={12} key={`chart-${index}`}>
                            {renderChart(chart)}
                        </Col>
                    ))}
                </Row>
            )}

            {/* Bottom Row - Last 4 KPI Cards */}
            {bottomKPIs.length > 0 && (
                <div className={styles.bottomSection}>
                    {bottomTitle && <h2 className={styles.sectionTitle}>{bottomTitle}</h2>}
                    <Row gutter={0} className={`${styles.cardsRow} ${styles.bottomRow}`}>
                        {bottomKPIs.map((kpi, index) => (
                            <Col xs={24} sm={12} md={12} lg={6} key={`bottom-${index}`}>
                                {renderKPICard(kpi, index + topKPIs.length + charts.length)}
                            </Col>
                        ))}
                    </Row>
                </div>
            )}
        </div>
    );
}

export default DashboardTemplate;
